import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Settings, Key, AlertCircle, Bell, Save, ExternalLink } from "lucide-react";

const SettingsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.account_type === "master_admin" || user?.account_type === "admin";
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [resetRequestDialogOpen, setResetRequestDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: ""
  });
  
  // Slack settings
  const [slackSettings, setSlackSettings] = useState({
    webhook_url: "",
    daily_report_time: "20:00",
    enabled: false
  });
  const [savingSlack, setSavingSlack] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchSlackSettings();
    }
  }, [isAdmin]);

  const fetchSlackSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/settings/slack`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.settings) {
        setSlackSettings(response.data.settings);
      }
    } catch (error) {
      console.error("Error fetching Slack settings:", error);
    }
  };

  const handleSaveSlackSettings = async () => {
    setSavingSlack(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/settings/slack`, slackSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success("Slack settings saved successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Slack settings");
    } finally {
      setSavingSlack(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/auth/change-password`,
        {
          old_password: passwordData.old_password,
          new_password: passwordData.new_password
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Password changed successfully");
      setPasswordDialogOpen(false);
      setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  const handleRequestPasswordReset = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/password-reset/request`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Password reset request submitted. Please wait for admin approval.");
      setResetRequestDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit reset request");
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Account Information */}
      <Card className="dark:bg-card dark:border-border">
        <CardHeader>
          <CardTitle className="text-gray-900 dark:text-white">Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-600 dark:text-gray-400">Name</Label>
              <p className="text-gray-900 dark:text-white font-medium mt-1">{user?.first_name}</p>
            </div>
            <div>
              <Label className="text-gray-600 dark:text-gray-400">Email</Label>
              <p className="text-gray-900 dark:text-white font-medium mt-1">{user?.email}</p>
            </div>
            <div>
              <Label className="text-gray-600 dark:text-gray-400">Account Type</Label>
              <p className="text-gray-900 dark:text-white font-medium mt-1 capitalize">
                {user?.account_type?.replace('_', ' ')}
              </p>
            </div>
            <div>
              <Label className="text-gray-600 dark:text-gray-400">Account Created</Label>
              <p className="text-gray-900 dark:text-white font-medium mt-1">
                {new Date(user?.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card className="dark:bg-card dark:border-border">
        <CardHeader>
          <CardTitle className="flex items-center text-gray-900 dark:text-white">
            <Key size={20} className="mr-2" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between p-4 bg-muted dark:bg-gray-700 rounded-lg">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white">Change Password</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Update your password to keep your account secure
              </p>
            </div>
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="change-password-button" variant="outline" className="ml-4">
                  Change Password
                </Button>
              </DialogTrigger>
              <DialogContent className="dark:bg-card">
                <DialogHeader>
                  <DialogTitle className="dark:text-white">Change Password</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="old_password">Current Password</Label>
                    <Input
                      id="old_password"
                      type="password"
                      data-testid="old-password-input"
                      value={passwordData.old_password}
                      onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                      className="dark:bg-gray-700 dark:border-gray-600"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      data-testid="new-password-input"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                      className="dark:bg-gray-700 dark:border-gray-600"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      data-testid="confirm-password-input"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                      className="dark:bg-gray-700 dark:border-gray-600"
                      required
                    />
                  </div>
                  <Button type="submit" data-testid="submit-password-change" className="w-full bg-blue-600 hover:bg-blue-700">
                    Update Password
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex items-start justify-between p-4 bg-muted dark:bg-gray-700 rounded-lg">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-white">Forgot Password?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Request a password reset from the admin
              </p>
            </div>
            <Dialog open={resetRequestDialogOpen} onOpenChange={setResetRequestDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="request-reset-button" variant="outline" className="ml-4">
                  Request Reset
                </Button>
              </DialogTrigger>
              <DialogContent className="dark:bg-card">
                <DialogHeader>
                  <DialogTitle className="dark:text-white">Request Password Reset</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex items-start space-x-3 p-4 bg-primary/10 dark:bg-primary/20 rounded-lg">
                    <AlertCircle className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="text-sm text-gray-900 dark:text-white">
                        Your request will be sent to an admin for approval. Once approved, you'll receive a temporary password to reset your account.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handleRequestPasswordReset}
                    data-testid="submit-reset-request"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Submit Request
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Slack Integration Settings - Admin Only */}
      {isAdmin && (
        <Card className="dark:bg-card dark:border-border">
          <CardHeader>
            <CardTitle className="flex items-center text-gray-900 dark:text-white">
              <Bell size={20} className="mr-2 text-purple-600" />
              Slack Integration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-300">
                Configure Slack to receive automated daily telecaller reports at 8 PM.
                <a 
                  href="https://api.slack.com/messaging/webhooks" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center ml-2 text-blue-600 hover:underline"
                >
                  How to get webhook URL <ExternalLink size={14} className="ml-1" />
                </a>
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted dark:bg-gray-700 rounded-lg">
                <div>
                  <Label className="font-semibold">Enable Daily Reports</Label>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Automatically send reports to Slack at scheduled time
                  </p>
                </div>
                <Switch
                  checked={slackSettings.enabled}
                  onCheckedChange={(checked) => setSlackSettings({ ...slackSettings, enabled: checked })}
                />
              </div>
              
              <div>
                <Label htmlFor="slack-webhook">Slack Webhook URL</Label>
                <Input
                  id="slack-webhook"
                  type="url"
                  placeholder="https://hooks.slack.com/services/..."
                  value={slackSettings.webhook_url}
                  onChange={(e) => setSlackSettings({ ...slackSettings, webhook_url: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:border-gray-600"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Paste your Slack Incoming Webhook URL here
                </p>
              </div>
              
              <div>
                <Label htmlFor="report-time">Daily Report Time</Label>
                <Input
                  id="report-time"
                  type="time"
                  value={slackSettings.daily_report_time}
                  onChange={(e) => setSlackSettings({ ...slackSettings, daily_report_time: e.target.value })}
                  className="mt-1 w-32 dark:bg-gray-700 dark:border-gray-600"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Time to send the daily report (default: 8:00 PM)
                </p>
              </div>
              
              <Button 
                onClick={handleSaveSlackSettings} 
                disabled={savingSlack}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Save size={16} className="mr-2" />
                {savingSlack ? "Saving..." : "Save Slack Settings"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;